// node modules
import { action, observable, autorun } from 'mobx';
import { cloneDeep } from 'lodash';
// modules
import { LStorage } from 'modules/Storage';
import { ServerIntervals } from './consts';
import { EntityId } from './components/TradingView/sources/charting_library.min';

export default class TradingChartsStore {
  // observables
  @observable chartInterval: TradingChartUserPeriod =
    LStorage.getItem('CHART_INTERVAL') || '1m';

  @observable isChartReadyToRender = false;

  @observable chartBarData: ChartBarData = {};

  @observable historicalDataRequestTimestamp: HistoricalDataRequestTimestamp
    = {};

  @observable openChartLayoutListModal = false;

  @observable chartSymbol = '';

  @observable isContinousChartChecked: ContinuosChartCheckedData = {};

  @observable continousChartInstrumentKeys: ContinuosChartInstrumentKeys = {};

  @observable isContinuousChartsApplicable: ContinuousChartApplicable = {};

  @observable activeSymbolData: DataSchema | null = null;

  @observable chartIntervalByExchangeToken: ChartIntervalByExchangeToken = {};

  @observable reloadLayout = false;

  @observable currentLayoutId: string | number = '';

  @observable upstoxLayoutId: string | number = '';

  @observable removedDrawingSourceIds = [] as EntityId[];

  // autoruns
  watchChartInterval = autorun(() => {
    LStorage.sync('CHART_INTERVAL', this.chartInterval);
  });

  // actions
  @action setChartInterval = (value: TradingChartUserPeriod) => {
    this.chartInterval = value;
  };

  @action setChartIntervalByExchangeToken =
  (exchangeToken: string, interval: ServerIntervals) => {
    this.chartIntervalByExchangeToken[exchangeToken] = interval;
  }

  @action setChartReadyToRender = (status: boolean) => {
    this.isChartReadyToRender = status;
  };

  @action setIsContinousChart = (exchangeToken: string, value: boolean) => {
    this.isContinousChartChecked[exchangeToken] = value;
  };

  @action toggleContinousChart = (exchangeToken: string) => {
    if (this.isContinousChartChecked[exchangeToken]) {
      this.isContinousChartChecked[exchangeToken] =
      !this.isContinousChartChecked[exchangeToken];
    } else {
      this.isContinousChartChecked[exchangeToken] = true;
    }
  };

  @action setIsContinuousChartsApplicable =
   (exchangeToken: string, value: boolean) => {
    this.isContinuousChartsApplicable[exchangeToken] = value;
  };

  @action setContinousChartInstrumentKey =
   (exchangeToken: string, continousChartInstrumentKey: string) => {
    this.continousChartInstrumentKeys[exchangeToken] =
    continousChartInstrumentKey;
  };

  @action setHistoricalDataRequestTimestamp = (
    symbolInterval: string,
    timestamp: number,
  ) => {
    this.historicalDataRequestTimestamp[symbolInterval] = timestamp;
  };

  @action deleteHistoricalDataRequestTimestamp = (
    symbolInterval: string,
  ) => {
    const updatedHistoricalTimestamp =
    cloneDeep(this.historicalDataRequestTimestamp);

    if (updatedHistoricalTimestamp[symbolInterval]) {
      delete updatedHistoricalTimestamp[symbolInterval];
    }

    this.historicalDataRequestTimestamp = updatedHistoricalTimestamp;
  };

  @action clearExistingChartUpdateIntervals = (
    subscriberToken: string,
  ) => {
    if (this.chartBarData[subscriberToken]) {
      clearTimeout(this.chartBarData[subscriberToken].initialTimeoutId!);
      clearTimeout(this.chartBarData[subscriberToken].timeoutId!);
      delete this.chartBarData[subscriberToken];
    }
  };

  @action setOpenChartLayoutListModal = (failed: boolean): void => {
    this.openChartLayoutListModal = failed;
  }

  @action setChartSymbol = (symbol: string): void => {
    this.chartSymbol = symbol;
  }

  @action setActiveSymbolData = (scripData: DataSchema): void => {
    this.activeSymbolData = scripData;
  }

  @action toggleReloadLayout() {
    this.reloadLayout = !this.reloadLayout;
  }

  @action setCurrentLayoutId(id: string | number) {
    this.currentLayoutId = id;
  }

  @action setUpstoxLayoutId(id: string | number) {
    this.upstoxLayoutId = id;
  }

  @action setRemovedDrawingSourceIds(ids: EntityId[]) {
    this.removedDrawingSourceIds = ids;
  }
}
